// src/Components/Modals/ParentApprovalRequiredModal.tsx
import React, { useMemo, useRef, useState } from "react";
import { usePurchaseReauth } from "../../Context/PurchaseReauthContext";
import { startParentInviteEmailCode } from "../Utils/purchaseReauth.api";
import Modal from "../Ui/Modal";

type Props = {
  onClose: () => void;
  /** Optional "Try again" flow that restarts reauth (not called on success). */
  onRetry: () => void;
  /** Prefill the guardian email if it is already known. */
  initialParentEmail?: string;
  /** True when a guardian email is already on file (entry becomes optional). */
  hasLinkedGuardian?: boolean;
};

function isValidEmail(email: unknown) {
  const e = String(email ?? "").trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

function onlyDigits(s: string) {
  return s.replace(/\D/g, "");
}

export default function ParentApprovalRequiredModal({ onClose, onRetry, initialParentEmail = "", hasLinkedGuardian = false }: Props) {
  const { submitParentCode } = usePurchaseReauth();

  const [parentEmail, setParentEmail] = useState<string>(String(initialParentEmail ?? ""));
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  // purchaseAuthId returned by /start-parent-invite (the challenge id)
  const [parentPurchaseAuthId, setParentPurchaseAuthId] = useState<string>("");
  const [codeDigits, setCodeDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string>("");
  const sendInFlightRef = useRef(false);

  const code = useMemo(() => codeDigits.join(""), [codeDigits]);
  const needsParentEmail = !hasLinkedGuardian && !initialParentEmail;
  const showEmailBlock = needsParentEmail || !!initialParentEmail || !hasLinkedGuardian;

  function resetCodeInputs() {
    setCodeDigits(["", "", "", "", "", ""]);
    setTimeout(() => inputRefs.current[0]?.focus(), 50);
  }

  async function sendCode() {
    if (sendInFlightRef.current) return;
    sendInFlightRef.current = true;
    setError("");
    const trimmed = String(parentEmail ?? "").trim().toLowerCase();

    if (needsParentEmail) {
      if (!isValidEmail(trimmed)) {
        setError("Enter a parent or guardian email.");
        sendInFlightRef.current = false;
        return;
      }
    } else if (trimmed && !isValidEmail(trimmed)) {
      setError("Enter a valid email.");
      sendInFlightRef.current = false;
      return;
    }

    try {
      setSending(true);
      const r = await startParentInviteEmailCode(trimmed);
      const newId = r?.purchaseAuthId;
      if (!newId) {
        setError("Couldn't start approval. Try again.");
        return;
      }
      setParentPurchaseAuthId(String(newId));
      setSent(true);
      resetCodeInputs();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Couldn't send the code.");
    } finally {
      setSending(false);
      sendInFlightRef.current = false;
    }
  }

  async function submitCode(codeOverride?: string) {
    setError("");
    const finalCode = onlyDigits((codeOverride ?? code).trim());
    if (finalCode.length !== 6) {
      setError("Enter the 6-digit code.");
      return;
    }
    if (!parentPurchaseAuthId) {
      setError("Send the code first.");
      return;
    }
    try {
      setVerifying(true);
      // Verifies and then resumes the original purchase.
      await submitParentCode(parentPurchaseAuthId, finalCode);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Wrong or expired code.");
    } finally {
      setVerifying(false);
    }
  }

  function handleDigitChange(index: number, raw: string) {
    setError("");
    const digits = onlyDigits(raw);
    if (digits.length >= 6) {
      const next = digits.slice(0, 6).split("");
      setCodeDigits(next);
      submitCode(next.join(""));
      return;
    }
    if (digits.length > 1) {
      const next = [...codeDigits];
      let cursor = index;
      for (const d of digits) {
        if (cursor > 5) break;
        next[cursor] = d;
        cursor += 1;
      }
      setCodeDigits(next);
      if (cursor <= 5) inputRefs.current[cursor]?.focus();
      else submitCode(next.join(""));
      return;
    }
    const next = [...codeDigits];
    next[index] = digits.slice(0, 1);
    setCodeDigits(next);
    if (digits.length === 1 && index < 5) inputRefs.current[index + 1]?.focus();
    if (next.every((d) => d.length === 1)) submitCode(next.join(""));
  }

  function handleDigitKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      if (codeDigits[index]) {
        const next = [...codeDigits];
        next[index] = "";
        setCodeDigits(next);
        setError("");
        return;
      }
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        const next = [...codeDigits];
        next[index - 1] = "";
        setCodeDigits(next);
        setError("");
      }
    }
    if (e.key === "ArrowLeft" && index > 0) inputRefs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < 5) inputRefs.current[index + 1]?.focus();
    if (e.key === "Enter") submitCode();
  }

  const canSubmit = code.length === 6 && !!parentPurchaseAuthId && !sending && !verifying;

  return (
    <Modal
      open
      onClose={onClose}
      tone="light"
      size="sm"
      eyebrow="Parent approval"
      title="Hand the device to a parent"
      className="dlg-gate"
      footer={
        <>
          <button type="button" className="mr-btn mr-btn--secondary" onClick={onRetry} disabled={sending || verifying}>
            Try again
          </button>
          <button type="button" className="mr-btn mr-btn--primary mr-btn--block" onClick={() => submitCode()} disabled={!canSubmit} aria-busy={verifying}>
            {verifying ? "Checking" : "Submit code"}
          </button>
        </>
      }
    >
      <p className="dlg-gate__lead">We email a 6-digit code to a parent. They type it here to approve.</p>

      {showEmailBlock && (
        <div className="dlg-field">
          <label className="mr-overline" htmlFor="parent-email">Parent email</label>
          <div className="dlg-inline">
            <input
              id="parent-email"
              className="dlg-input"
              value={parentEmail}
              onChange={(e) => {
                setParentEmail(e.target.value);
                setSent(false);
                setParentPurchaseAuthId("");
                setError("");
              }}
              placeholder="parent@example.com"
              inputMode="email"
              autoComplete="email"
              disabled={sending || verifying}
            />
            <button type="button" className="mr-btn mr-btn--secondary mr-btn--sm" onClick={sendCode} disabled={sending || verifying} aria-busy={sending}>
              {sending ? "Sending" : sent ? "Resend" : "Send code"}
            </button>
          </div>
          <span className="dlg-field__note">
            {needsParentEmail ? "This email is saved for future approvals." : "Changing this email uses the new one for approval."}
          </span>
        </div>
      )}

      <div className="dlg-code" aria-label="Enter approval code">
        <span className="mr-overline">Enter code</span>
        <div className="dlg-code__row">
          {codeDigits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              className="dlg-code__box"
              value={d}
              onChange={(e) => handleDigitChange(i, e.target.value)}
              onKeyDown={(e) => handleDigitKeyDown(i, e)}
              inputMode="numeric"
              autoComplete={i === 0 ? "one-time-code" : "off"}
              aria-label={`Digit ${i + 1}`}
              disabled={verifying}
            />
          ))}
        </div>
      </div>

      {error ? <div className="dlg-note dlg-note--danger" role="alert">{error}</div> : null}
    </Modal>
  );
}
